import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SUPPORTED_LANGUAGES, LANGUAGE_STORAGE_KEY } from '@/utils/language';

interface MobileLanguageNavProps {
  /** Show the "Don't speak Polish?" hint under the options (logged-out). */
  showHint?: boolean;
}

/**
 * In-drawer language switcher for the mobile menu (logged-out / onboarding). The
 * collapsed row shows the current language + a chevron; tapping it expands an
 * inline list of the *other* languages (and, when logged out, the "Don't speak
 * Polish?" hint) within the panel — not a floating dropdown. Persists the choice
 * the same way the header/Settings switchers do.
 */
const MobileLanguageNav = ({ showHint = false }: MobileLanguageNavProps) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const choose = (lng: string) => {
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
    } catch {
      /* persistence is best-effort */
    }
    setOpen(false);
  };

  const others = SUPPORTED_LANGUAGES.filter((lng) => lng !== i18n.language);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-1 py-2 text-sm text-foreground transition-colors hover:text-primary"
      >
        <span>{t(`language_switcher.${i18n.language}`)}</span>
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="flex flex-col pl-3">
          {others.map((lng) => (
            <button
              key={lng}
              type="button"
              onClick={() => choose(lng)}
              className="px-1 py-2 text-left text-sm text-foreground transition-colors hover:text-primary"
            >
              {t(`language_switcher.${lng}`)}
            </button>
          ))}
          {showHint && (
            <div className="px-1 py-2 text-xs text-muted-foreground">
              <p>{t('language_switcher.hint_question')}</p>
              <p className="font-medium underline underline-offset-2">
                {t('language_switcher.hint_answer')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileLanguageNav;
